import type { ILink, IPagePath } from "@contentful-api/types/contentful";
import { getAssetFileUrl } from "@contentful-api/helpers/getAssetFileUrl";
import { isAllowedExternalUrl } from "@contentful-api/helpers/isAllowedExternalUrl";
import { isPagePath, withQueryParameters } from "./utils";
import type { LinkModel } from "./types";

export const mapLink = (link?: ILink | IPagePath): LinkModel | undefined => {
    if (!link?.fields) return undefined;

    const id = link.sys.id;

    if (isPagePath(link)) {
        return { id, type: "url", label: link.fields.label, href: link.fields.completeSlug };
    }

    const { fields } = link;
    const label = fields.label ?? "";

    if (fields.modal) {
        return {
            id,
            type: "modal",
            label,
            modalType: fields.modal,
            description: fields.description,
            descriptionRichText: fields.descriptionRichText,
            target: fields.target,
        };
    }

    // Only a new tab is sent, so same-tab links stay lean.
    const toUrl = (href: string, openInNewTab?: boolean): LinkModel => ({
        id,
        type: "url",
        label,
        href,
        ...(openInNewTab && { openInNewTab: true as const }),
    });

    const pageHref = fields.internalLink?.fields.completeSlug;
    if (pageHref) {
        return toUrl(
            withQueryParameters(pageHref, fields.queryParameters),
            fields.internalLinkOnNewTab
        );
    }

    if (fields.deepLink) return toUrl(`#${fields.deepLink}`);

    if (fields.externalUrl && isAllowedExternalUrl(fields.externalUrl)) {
        return toUrl(fields.externalUrl, fields.internalLinkOnNewTab);
    }

    const assetHref = getAssetFileUrl(fields.linkAsset);
    // Assets such as PDFs always open in a new tab.
    return assetHref ? toUrl(assetHref, true) : undefined;
};

// Leaves out entries that cannot become a link, e.g. a link without a target.
export const mapLinks = (links: readonly (ILink | IPagePath | undefined)[] = []): LinkModel[] =>
    links.flatMap((link) => mapLink(link) ?? []);
